import { useState } from "react";
import { useTranslation } from "react-i18next";
import { MessageSquarePlus, Loader2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { useAddDefectComment } from "@/hooks/useDefectComments";

interface DefectCommentFormProps {
  defectId: string;
  defectTitle: string;
  assignedToUserId?: string | null;
  onSuccess?: () => void;
}

export function DefectCommentForm({
  defectId,
  defectTitle,
  assignedToUserId,
  onSuccess,
}: DefectCommentFormProps) {
  const { t } = useTranslation();
  const [content, setContent] = useState("");
  const { mutate: addComment, isPending } = useAddDefectComment();

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();

    if (!content.trim()) return;

    addComment(
      {
        defectId,
        content: content.trim(),
        defectTitle,
        assignedToUserId,
      },
      {
        onSuccess: () => {
          setContent("");
          onSuccess?.();
        },
      },
    );
  };

  return (
    <form onSubmit={handleSubmit} className="space-y-3" data-testid="defect-comment-form">
      <Textarea
        value={content}
        onChange={(e) => setContent(e.target.value)}
        placeholder={t("defects.commentPlaceholder")}
        className="min-h-[80px] resize-none"
        disabled={isPending}
      />
      <div className="flex justify-end">
        <Button type="submit" size="sm" disabled={isPending || !content.trim()}>
          {isPending ? (
            <Loader2 className="h-4 w-4 mr-2 animate-spin" />
          ) : (
            <MessageSquarePlus className="h-4 w-4 mr-2" />
          )}
          {t("defects.sendComment")}
        </Button>
      </div>
    </form>
  );
}
